import moment from "moment";
import queryString from "query-string";
import React from "react";
import { useTranslation } from "react-i18next";
import WeekControl from "../../../components/Home/WeekControl";
import ToastUtil from "../../../components/ToastNotification/ToastUtil";
import { config } from "../../../config/index";
import { toWeekEndDate, toWeekStartDate } from "../../../helpers/moment";
import ShiftWeekSchedule from "./ShiftWeekSchedule";
import "./styles.css";

const { API_URL } = config;

const SchedulePreference = ({
  history,
  client,
  sites,
  location: { search },
}) => {
  const { t } = useTranslation();
  const params = queryString.parse(search);
  const token = localStorage.getItem("token");
  const activeSite = localStorage.getItem("default_site_id")
    ? Number(localStorage.getItem("default_site_id"))
    : null;
  const [activeDate, setActiveDate] = React.useState(moment(params.date));
  const [siteShifts, setSiteShifts] = React.useState([]);
  const [activeWeek, setActiveWeek] = React.useState({
    startDate: toWeekStartDate(moment()),
    endDate: toWeekEndDate(moment()),
  });
  const [preferredShifts, setPreferredShifts] = React.useState([]);
  const [schedules, setSchedules] = React.useState([]);
  const [defaultSiteData, setDefaultSiteData] = React.useState(null);
  const [showMessage, setShowMessage] = React.useState(false);
  const componentMounted = React.useRef(true);

  React.useEffect(() => {
    sites && defaultSiteHandler();
  }, [sites]);

  React.useEffect(() => {
    client &&
      setActiveWeek({
        startDate: toWeekStartDate(moment(), client.start_day),
        endDate: toWeekEndDate(moment(), client.start_day),
      });
  }, [client]);

  const defaultSiteHandler = () =>
    setDefaultSiteData(sites.find((site) => site.id === activeSite));

  const nextWeekHandler = () =>
    setActiveWeek({
      startDate: activeWeek.startDate.add(1, "week"),
      endDate: activeWeek.endDate.add(1, "week"),
    });

  const prevWeekHandler = () =>
    setActiveWeek({
      startDate: activeWeek.startDate.subtract(1, "week"),
      endDate: activeWeek.endDate.subtract(1, "week"),
    });

  // Fetch Site Shifts
  React.useEffect(() => {
    const fetchSiteShifts = async () => {
      let url = `${API_URL}/site/${activeSite}/shifts`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let shiftsData = data.data.sort(
            (a, b) =>
              moment(a.start_time, "HH:mm:ss") -
              moment(b.start_time, "HH:mm:ss")
          );
          // if (activeSite === 1)
          //   shiftsData = moment(activeDate).isBefore(moment("2020-04-19"))
          //     ? shiftsData.filter((x) => [1, 2, 3, 7].includes(x.id))
          //     : shiftsData.filter((x) => x.id > 3);

          setSiteShifts(shiftsData);
        } else {
          ToastUtil.error(data.message);
        }
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchSiteShifts();
  }, [activeSite, activeDate]);

  React.useEffect(() => {
    const fetchSchedule = async () => {
      let url = `${API_URL}/associate/myschedule`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        if (componentMounted.current) {
          let data = await response.json();
          data.success
            ? setSchedules(data.data)
            : ToastUtil.error(data.message);
        }
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    fetchSchedule();
    return () => {
      // This code runs when component is unmounted
      componentMounted.current = false; // set it to false when we leave the page
    };
  }, [token]);

  // Fetch User Preferred Shifts
  React.useEffect(() => {
    const fetchShiftPreference = async () => {
      let url = `${API_URL}/associate/sites/${activeSite}/preference`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let filtered = data.data.sort(
            (a, b) =>
              moment(a.shift.start_time, "HH:mm:ss") -
              moment(b.shift.start_time, "HH:mm:ss")
          );
          setPreferredShifts(filtered);
        } else {
          ToastUtil.error(data.message);
        }
        // data.success ? setSiteShifts(data.data) : ToastUtil.error(data.message)
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchShiftPreference();
  }, [activeDate, activeSite]);

  React.useEffect(() => {
    if (!defaultSiteData) {
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);

      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, []);

  return (
    <React.Fragment>
      {defaultSiteData?.enable_preferred_schedule && (
        <div className="schedule-preference__container jumbotron">
          <div className="rounded-0 mb-0 dashboard__jumbotron-container preferred__label-container">
            <div className="preferred__label-wrapper">
              <div>
                <i
                  className="fas fa-arrow-left"
                  style={{
                    marginTop: 5,
                  }}
                  onClick={() => {
                    history.goBack();
                  }}
                />
              </div>
              <div
                style={{
                  marginLeft: 20,
                }}
              >
                <h4 className="text-brand">{t("Preferred Schedule")}</h4>
                <p className="lead">{moment().format("dddd, DD MMMM YYYY")}</p>
              </div>
            </div>
            <div>
              <h5>{defaultSiteData?.name || ""}</h5>
              {sites.length > 1 && (
                <p>
                  <a href="/site-settings">Change</a>
                </p>
              )}
            </div>
          </div>

          <div className="schedule-preference__body">
            {client && client.enable_smart_scheduler === true && (
              <React.Fragment>
                <WeekControl
                  title=" "
                  client={client}
                  activeWeek={activeWeek}
                  nextWeek={nextWeekHandler}
                  prevWeek={prevWeekHandler}
                />
                <ShiftWeekSchedule
                  client={client}
                  sites={sites}
                  activeWeek={activeWeek}
                  siteShifts={siteShifts}
                  activeSite={activeSite}
                  activeDate={activeDate}
                  preferredShifts={preferredShifts}
                  schedules={schedules}
                  setPreferredShifts={setPreferredShifts}
                />
              </React.Fragment>
            )}
          </div>
        </div>
      )}

      {showMessage && !defaultSiteData?.enable_preferred_schedule && (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <h5 className="text-muted">Preferred schedule is not enable.</h5>
          <p className="text-muted">Please contact the MP.</p>
        </div>
      )}
    </React.Fragment>
  );
};

export default SchedulePreference;
